import InfoIcon from "src/assets/icons/SsqTitleIcon/info.svg?react";
import MilkPot from "src/assets/icons/milk-bottle-small.svg?react";
import PeopleIcon from "src/assets/icons/users-small.svg?react";
import FormIcon from "src/assets/icons/SsqTitleIcon/form.svg?react";
import { FoodsFormBase, type FoodsPage } from "@foodpilot/foods";
import { Box, useTheme } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import type { Company } from "src/api/client-api/company/useGetOneOrganization";
import type { SupplierFormType } from "./SupplierForm.types";
import { Page1 } from "./Pages/Page1";
import { Page2 } from "./Pages/Page2";
import { Page3 } from "./Pages/Page3";
import { Page4 } from "./Pages/Page4";

type SupplierFormLegacyViewProps = {
    existingSupplier?: Company;
    isNew: boolean;
    isSaving: boolean;
    onConfirmExit: () => void;
    onValidate: () => Promise<boolean>;
    setSupplier: Dispatch<SetStateAction<SupplierFormType>>;
    startingPageId: string;
    subtitle: string;
    supplier: SupplierFormType;
};

/** The original four-step supplier wizard, retained for the UX-v1 rollout path. */
export const SupplierFormLegacyView = ({
    existingSupplier,
    isNew,
    isSaving,
    onConfirmExit,
    onValidate,
    setSupplier,
    startingPageId,
    subtitle,
    supplier,
}: SupplierFormLegacyViewProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const pageId = Number.parseInt(startingPageId);
    const title =
        isNew ?
            t("creation_d_un_nouveau_fournisseur", "Create a new supplier")
        :   t("modifier_le_fournisseur", "Edit supplier");
    const pages: FoodsPage[] = [
        {
            title: t("general_information", "General information"),
            icon: <InfoIcon />,
            content: (
                <Page1
                    appearance="legacy"
                    supplier={supplier}
                    setSupplier={setSupplier}
                    existingSupplier={existingSupplier}
                />
            ),
        },
        {
            title: t("utilisateurs", "Contributors"),
            icon: <PeopleIcon />,
            content: (
                <Page2
                    appearance="legacy"
                    supplier={supplier}
                    setSupplier={setSupplier}
                    existingSupplier={existingSupplier}
                />
            ),
        },
        {
            title: t("menu.products.ingredients", "Ingredients"),
            icon: <MilkPot />,
            content: (
                <Page3
                    appearance="legacy"
                    supplier={supplier}
                    setSupplier={setSupplier}
                    existingSupplier={existingSupplier}
                />
            ),
        },
        {
            title: t("summary_of_data_entered", "Summary of data entered"),
            icon: <FormIcon />,
            content: <Page4 appearance="legacy" supplier={supplier} />,
        },
    ];

    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto", padding: theme.spacing(5.5, 6.5) }}>
            <FoodsFormBase
                showSync={false}
                isLoading={isSaving}
                startingPage={pageId}
                onClose={onConfirmExit}
                textOptions={{
                    title,
                    subtitle,
                    nextPage: t("etape_suivante", "Next step"),
                    previousPage: t("previous", "Previous"),
                    finalPage: t("sauvegarder", "Save"),
                }}
                pages={pages}
                onValidate={onValidate}
                confirmExit={true}
                confirmOptions={{
                    titleText:
                        isNew ?
                            t(
                                "etes_vous_sur_de_vouloir_quitter_la_creation_du_fournisseur",
                                "Are you sure you want to exit supplier creation?",
                            )
                        :   t(
                                "etes_vous_sur_de_vouloir_quitter_la_modification_du_fournisseur",
                                "Are you sure you want to exit supplier editing?",
                            ),
                    confirmText:
                        isNew ? t("common.close", "Close") : t("ne_pas_enregistrer_et_quitter", "Exit without saving"),
                    cancelText:
                        isNew ?
                            t("ne_pas_quitter_la_creation", "Continue creating")
                        :   t("ne_pas_quitter_la_modification", "Continue editing"),
                    onConfirm: onConfirmExit,
                    onCancel: () => {
                        // Keep the shared controller's current draft open.
                    },
                }}
            />
        </Box>
    );
};
